import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Loader2, ExternalLink, ClipboardList } from "lucide-react";
import { EBRC } from "@/constants/testIds";

// Sandbox state machine, minus the `self_cert_pending` intermediate step
// (used to exist only for the removed Simulate button to advance into).
// After withdrawal, we jump `payment_received` → `irm_generated` immediately,
// and the guide's step-6 confirmation button jumps `irm_generated` → `self_certified`.
const STATES = [
  { key: "payment_received", label: "Payment received" },
  { key: "irm_pending", label: "IRM pending" },
  { key: "irm_generated", label: "IRM generated" },
  { key: "self_certified", label: "eBRC self-certified" },
];

const DGFT_URL = "https://www.dgft.gov.in/CP/?opt=ebrc";

export function EbrcTracker({ status }) {
  // If the incoming status is the legacy `self_cert_pending`, collapse it to
  // `irm_generated` so the tracker still renders correctly for older records.
  const displayStatus = status === "self_cert_pending" ? "irm_generated" : status;
  const idx = Math.max(0, STATES.findIndex((s) => s.key === displayStatus));
  const isDone = displayStatus === "self_certified";

  return (
    <Card className="border" data-testid={EBRC.tracker}>
      <CardContent className="p-6 md:p-8">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
              <ClipboardList className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                eBRC / DGFT tracker
              </p>
              <h2 className="font-display text-2xl font-bold mt-1">
                {isDone ? "Self-certified" : STATES[idx].label}
              </h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                {isDone
                  ? "Your eBRC has been self-certified on DGFT. Keep the eFIRA on file for audits."
                  : "Track each stage from bank realisation to DGFT self-certification, all in one place."}
              </p>
            </div>
          </div>
        </div>

        <ol className="space-y-4">
          {STATES.map((s, i) => {
            const done = i < idx;
            const active = i === idx;
            return (
              <li key={s.key} className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0">
                  {done ? (
                    <CheckCircle2 className="h-5 w-5 text-[#1E8A5B]" />
                  ) : active ? (
                    <div className="h-5 w-5 rounded-full bg-primary grid place-items-center">
                      <div className="h-2 w-2 rounded-full bg-white" />
                    </div>
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground/40" />
                  )}
                </div>
                <div className="flex-1">
                  <p
                    className={`text-sm ${
                      done
                        ? "text-muted-foreground line-through"
                        : active
                          ? "text-foreground font-semibold"
                          : "text-muted-foreground"
                    }`}
                  >
                    {s.label}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

// canSelfCertify: only render the "I've self-certified" action once the IRM
// has been generated. Confirming jumps status → `self_certified`.
export function SelfCertifyGuide({ dgft, status, onSelfCertify, certifying }) {
  const shippingBill = dgft?.shipping_bill_number || "your Shipping Bill";
  const gstInv = dgft?.gst_invoice_number || "the GST invoice";
  const displayStatus = status === "self_cert_pending" ? "irm_generated" : status;
  const canSelfCertify = displayStatus === "irm_generated";
  const isDone = displayStatus === "self_certified";

  return (
    <Card className="border-primary/20 bg-primary/5" data-testid={EBRC.guide}>
      <CardContent className="p-6 md:p-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold text-primary">Self-Certify Your eBRC</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              The bank has issued an Inward Remittance Message (IRM). Follow these steps on the DGFT portal to
              self-certify your eBRC.
            </p>
          </div>
        </div>

        <ol className="space-y-4">
          <GuideStep n={1} title="Log in to the DGFT portal">
            Sign in with your IEC credentials at{" "}
            <a
              href={DGFT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium hover:underline"
              data-testid={EBRC.dgftLink}
            >
              dgft.gov.in <ExternalLink className="inline h-3 w-3 ml-0.5" />
            </a>
            . If this is your first visit, complete the one-time IEC profile setup.
          </GuideStep>
          <GuideStep n={2} title="Open the eBRC self-certification module">
            Navigate to <em>Services → eBRC → View / Certify IRM</em>. Filter by <strong>this month</strong> to
            surface the IRM the bank just issued for this payment.
          </GuideStep>
          <GuideStep n={3} title="Link the IRM to your Shipping Bill">
            Select the IRM for this payment and link it to Shipping Bill{" "}
            <span className="sf-mono font-medium">{shippingBill}</span>. If you have multiple IRMs for the same
            shipment, DGFT will let you allocate the amount across them.
          </GuideStep>
          <GuideStep n={4} title="Confirm the GST invoice matches">
            Verify the GST Invoice Number <span className="sf-mono font-medium">{gstInv}</span> and its date on the
            DGFT screen match the values you entered in Flo Payments. If they mismatch, edit on the DGFT side to align.
          </GuideStep>
          <GuideStep n={5} title="Submit self-certification">
            Click <strong>Self-Certify</strong>, review the summary, and confirm. DGFT will email you the certified
            eBRC PDF within a few minutes.
          </GuideStep>
          <GuideStep n={6} title="Confirm here once DGFT has certified">
            After DGFT confirms your eBRC, come back and mark it certified so this transaction is filed in your
            audit trail alongside the eFIRA.
            {canSelfCertify && onSelfCertify && (
              <div className="mt-3">
                <Button
                  onClick={onSelfCertify}
                  disabled={certifying}
                  size="sm"
                  data-testid={EBRC.selfCertifyBtn}
                >
                  {certifying ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Recording…
                    </>
                  ) : (
                    <>I&apos;ve self-certified on DGFT</>
                  )}
                </Button>
              </div>
            )}
            {isDone && (
              <p className="mt-3 text-xs font-semibold text-[#1E8A5B]">
                <CheckCircle2 className="inline h-4 w-4 mr-1" /> Marked self-certified in Flo Payments.
              </p>
            )}
          </GuideStep>
        </ol>

        <a
          href={DGFT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-primary hover:underline"
        >
          Open DGFT portal <ExternalLink className="h-4 w-4" />
        </a>
      </CardContent>
    </Card>
  );
}

function GuideStep({ n, title, children }) {
  return (
    <li className="flex gap-4">
      <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-semibold text-sm shrink-0">
        {n}
      </div>
      <div>
        <p className="font-semibold text-foreground mb-1">{title}</p>
        <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
      </div>
    </li>
  );
}
